/**
 * Language-use scheme group grading — ONE JOB PER (SUBMISSION × GROUP), the third phase of a grading wave.
 *
 * MULTI-MEMBER SCHEMES ONLY. Every language-use rule is a `LanguageUseScheme`, whether it covers one task or
 * several, but a SOLO one (a single `dependsOn` entry) is graded inline with its task's content call and never
 * reaches this job — see `getSoloLanguageUseRule`, which is the one place that split is decided, and
 * `relevantLanguageUseGroupIds`, which is the one place it is enumerated. Only a solo rule's result WRITE is
 * deferred, by `queueSoloLanguageUseWrite`, into the same `languageUseRuleResults` array this job's outcome
 * lands in.
 *
 * WHY A MULTI-MEMBER ONE CANNOT RIDE ALONG WITH THE TASK. It depends on several main tasks — the tasks were split
 * apart but form one continuous piece of writing — and is graded as ONE call over all of them. A `grading` row
 * covers one (main task × submission), so a group spanning three tasks would be graded three times if each row
 * graded what it found. Inside a single run an in-memory set prevented that; across rows nothing can hold one, so
 * the unit has to become the group.
 *
 * DISPATCHED UP FRONT, alongside the wave's `grading` and `gradingConsistency` rows, and that is a change from
 * when the content handler enqueued it on discovering the group. Three things came out of that move:
 *  - the row exists from the start, so the canvas spinner for a language-use box appears when grading STARTS
 *    rather than only once every content row has finished;
 *  - a caller that waits for the wave can see it, which is what stopped the chat announcement firing while this
 *    phase was still running;
 *  - it can no longer be enqueued twice. A `done` row does not hold its dedupe key, so once this phase could
 *    finish before a sibling content row, that row's follow-up enqueue would have inserted a SECOND row and
 *    graded the group again.
 * `batch.units` below is therefore the only thing that decides a group is graded, and it has to reproduce the
 * old runtime predicate exactly — see the comments on each filter.
 *
 * A REAL GRADE COMPONENT, which is why it is a job at all and not fire-and-forget like the FI summary work. It
 * writes a score, so the wave is not over until it has run — hence its membership of the `grading` step
 * (`EXTRA_STEP_TYPES`), which is what keeps the announcement from firing over grades this phase is about to change.
 *
 * It also owns its progress unit at both ends — see `runLanguageUseGroupForSubmission`.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../../exam.js';
import { toNormalizedTaskRefSet } from '../../../utils/task-ref.js';
import type { LanguageUseSchemeLike } from '../../../../shared/grammar-rules-resolution.js';
import { relevantLanguageUseGroupIds } from '../../agents/gradingAgent.js';
import { runLanguageUseGroupForSubmission } from '../../agents/grading-phases.js';
import { readySubmissionIds } from '../../../utils/exam-utils.js';
import { settling } from '../job-conditions.js';
import { anyLiveJobForTypesWhere } from '../job-store.js';
import { submissionLanguageUseLockKey } from '../job.js';
import { gradingStep } from '../steps/grading.step.js';
import { defineJob } from '../registry.js';
import type { GradingScope } from './grading.job.js';

export interface LanguageUseGroupGradingPayload {
  submissionId: string;
  /** The LanguageUseScheme's `_id`. Identity, so a renumbered task tree cannot misdirect this row. */
  schemeId: string;
  /** Whether this row waits for the exam to SETTLE before it runs — see `GradingPayload.settle`. */
  settle?: boolean;
  [key: string]: unknown;
}

export const languageUseGroupGradingJob = defineJob<LanguageUseGroupGradingPayload>({
  type: 'languageUseGroupGrading',

  /**
   * THE SHARED DECLARATION — the same object every type of the `grading` step points at, because the completion that
   * runs is the terminating row's own (see `steps/grading.step.ts`).
   */
  onStepComplete: gradingStep,

  /**
   * ONE ROW PER (SUBMISSION × MULTI-MEMBER GROUP) in scope, enumerated from the exam rather than discovered by a
   * content run. Every filter here mirrors what the runtime path used to decide, and dropping any of them would
   * make this dispatch grade something the old one did not:
   *
   *  - a CONTENT-only request grades no group at all unless it is a genuine first grade batching in its solo
   *    rules (`includeSoloLanguageUse`) — the old `target === 'content' && !includeSoloLanguageUse` suppression;
   *  - `languageUseSchemeId` scopes the wave to the one rule a caller just edited, since a task can belong to
   *    several groups and the others did not change;
   *  - `taskRefs` are matched against the scheme's MEMBERS inside `relevantLanguageUseGroupIds`, not against the
   *    main tasks they sit under, so regrading one group leaves a sibling group on the same task alone.
   *
   * A row whose member tasks turn out to be absent for that submission degrades to `{ skipped: true }` in the
   * handler, which costs a row and a spinner rather than a grade — the enumeration deliberately does not read
   * every submission's tree to pre-empt that.
   */
  batch: {
    units: async (examId, scope) => {
      const { taskRefs, target, languageUseSchemeId, includeSoloLanguageUse, submissionIds, settle } =
        scope as GradingScope;
      if ((target ?? 'content') === 'content' && !includeSoloLanguageUse) return [];

      const examCollection = await getCollection(Exam as any);
      const exam = await examCollection.findOne({ _id: examId } as any, {
        projection: { tasks: 1, languageUseSchemeRules: 1 },
      } as any);
      const examTasks: any[] = Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
      const rules: LanguageUseSchemeLike[] = Array.isArray((exam as any)?.languageUseSchemeRules)
        ? (exam as any).languageUseSchemeRules
        : [];

      const requestedRefs = taskRefs?.length ? toNormalizedTaskRefSet(taskRefs) : null;
      const schemeIds = relevantLanguageUseGroupIds(examTasks, rules, requestedRefs).filter(
        id => !languageUseSchemeId || id === languageUseSchemeId,
      );
      if (!schemeIds.length) return [];

      const targetSubmissionIds = submissionIds?.length ? submissionIds.map(String) : await readySubmissionIds(examId);

      return targetSubmissionIds.flatMap(submissionId =>
        schemeIds.map(schemeId => ({ submissionId, schemeId, ...(settle ? { settle: true } : {}) })),
      );
    },
    /** A caller that waits for the wave has to wait on the row actually doing the work, not on nothing. */
    joinTwin: true,
    waitMs: 10 * 60_000,
  },

  /**
   * Per (SUBMISSION × SCHEME), which is exactly what this row writes: one element of that submission's
   * `languageUseRuleResults`, addressed by scheme id (`upsertLanguageUseRuleResult`). That array is top-level and
   * disjoint from `taskSubmissions`, so this grades alongside the content grading of its own member tasks rather
   * than behind every one of them — which is what it used to do, sharing `submission:<id>` with them.
   */
  lockKey: (_examId, payload) => submissionLanguageUseLockKey(payload.submissionId, payload.schemeId),

  /**
   * `settle` is part of the key for the same reason it is part of `grading`'s: a row a teacher's turn is waiting
   * on must not be swallowed by a queued background re-grade that is still sitting out its settle window.
   */
  dedupeKey: (examId, payload) =>
    `languageUseGroupGrading:${examId}:${payload.submissionId}:${payload.schemeId}:${payload.settle ? 'settle' : 'now'}`,

  /**
   * NOTHING TO WAIT FOR IN THE GRADES — this phase reads a submission's ANSWERS and compares nothing, so its
   * member tasks' content grades are irrelevant to it. What it does wait for is the two things that would make
   * those answers a moving target.
   *
   * THE SETTLE WINDOW, shared with every other phase of the wave (`settling`), so they cannot wait out different
   * windows. It needs one at all only because it is dispatched up front now rather than created by a content run
   * that had already waited: one upload changing three things should still produce one grading wave, not three.
   *
   * ANSWER EXTRACTION, for the same reason `grading` waits on it and with the same one-sided shape (gating both
   * directions would livelock — see the gate in `grading.job.ts`). Extraction holds `submission:<id>` and `$set`s
   * the whole `taskSubmissions` array, and this row READS that array to resolve its members
   * (`runLanguageUseGroupForSubmission`). Sharing the submission lock used to make the two mutually exclusive for
   * free; the narrower per-scheme lock does not, so the exclusion has to be stated.
   */
  ready: async (exam, job) => {
    if (settling(exam, job)) return { verdict: 'wait', because: 'languageUseGroupGrading:exam-not-settled' };

    if (await anyLiveJobForTypesWhere(job.examId, ['answerExtraction'], { submissionId: job.payload.submissionId })) {
      return { verdict: 'wait', because: 'languageUseGroupGrading:answers-extracting' };
    }
    return 'run';
  },

  /** One model call over a few tasks' answers. */
  maxRuntimeMs: 8 * 60_000,
  maxAttempts: 2,

  handler: async (examId, payload) => {
    const { graded, note } = await runLanguageUseGroupForSubmission(examId, payload.submissionId, payload.schemeId);
    if (!graded) {
      // The scheme or the submission is gone, or no member task is present. Nothing a retry fixes, and the grades
      // that DO exist are untouched — so a skip, with the reason on the row.
      logger.info({ examId, ...payload, note }, 'Language-use group grading had nothing to do');
      return { skipped: true, note };
    }
    return { note, result: { gradedSubmissionIds: [payload.submissionId], schemeId: payload.schemeId } };
  },
});
